import { SITE_ARCHE_PATH, HERO } from './arches/fias-site/content';

/**
 * Registry of internal arches published as websites.
 *
 * A "site arche" serves real, crawlable HTML at its canonical URL instead of
 * the SPA's empty shell. Listing an arche here is what causes the build to
 * render it to a static snapshot under `dist/_prerendered/`, which CloudFront
 * then serves for that URL (see infra/cdk/lib/frontend-viewer-function.ts).
 *
 * Only pages that render with no auth, no data fetching, and no browser APIs
 * on first paint belong here — anything else will either throw during the
 * server render or produce a snapshot that lies about what a visitor sees.
 *
 * Third-party plugin arches opt in through their manifest instead and are
 * rendered inside the sandboxed plugin build; this registry is the internal
 * half of the same feature.
 */

export interface SiteArcheRoute {
  /**
   * Path relative to the arche's mount point. '/' is the front page.
   * Becomes `_prerendered/a/<slug>[/<sub>]/index.html`.
   */
  path: string;
  /** `<title>`. Shown in search results and the browser tab. */
  title: string;
  /** `<meta name="description">`. Search snippet and link-preview subtitle. */
  description: string;
}

export interface SiteArche {
  /** The arche's id, for cross-referencing the catalog. */
  archeId: string;
  /** Slug the arche is served under. Must match `arches.slug`. */
  slug: string;
  /** Human name, used for og:site_name. */
  name: string;
  routes: SiteArcheRoute[];
  /**
   * When set, this arche is ALSO the domain root, and its front page is
   * snapshotted to `_prerendered/root/index.html`. At most one arche should
   * claim the root.
   */
  servesDomainRoot?: boolean;
  /**
   * Who writes this arche's snapshots. `'build'` (the default): the build,
   * from `routes`. `'catalog'`: they are rendered from live data whenever it
   * changes (the Games page, ADR-035/036), so the build writes none and
   * `routes` stays empty — one writer per prefix.
   */
  producer?: 'build' | 'catalog';
}

export const SITE_ARCHES: SiteArche[] = [
  {
    archeId: 'arche_fias_site',
    slug: 'fias-site',
    name: 'Fias',
    servesDomainRoot: true,
    routes: [
      {
        path: '/',
        title: 'Fias — the world’s platform for AI monetization',
        description: HERO.subhead,
      },
    ],
  },
  {
    // The public Games page (ADR-035): every game on Fias. Its pages are the
    // catalog's, so the build renders none of them.
    archeId: 'arche_games',
    slug: 'games',
    name: 'Fias Games',
    producer: 'catalog',
    routes: [],
  },
];

/**
 * True when `segment` is the slug of an internal arche published as a website.
 *
 * This registry — not `arches.arche_type` — is what decides whether an arche
 * serves a prerendered snapshot, so it is also what decides whether its slug
 * URL is canonical (see ConditionalProtectedRoute). `arche_type = 'site'` is
 * the MARKETPLACE taxonomy that groups the Arche Store and Learn About Fias
 * under the store's "Sites" filter; those two are ordinary internal arches
 * dispatched by id, and treating them as site arches strands their slug URLs
 * on the plugin iframe renderer.
 */
export function isSiteArcheSlug(segment: string | undefined): boolean {
  if (!segment) return false;
  return SITE_ARCHES.some((a) => a.slug === segment);
}

/**
 * The slug of the internal site arche with this id (`arche_games` → `games`),
 * or null. A site arche's slug URL is its canonical address — the one its
 * snapshot is served at — so its id URL is rewritten to it.
 */
export function siteArcheSlugForId(archeId: string): string | null {
  return SITE_ARCHES.find((a) => a.archeId === archeId)?.slug ?? null;
}

/** Canonical URL path a site-arche route is served at. */
export function siteArcheRoutePath(arche: SiteArche, route: SiteArcheRoute): string {
  return route.path === '/' ? `/a/${arche.slug}` : `/a/${arche.slug}${route.path}`;
}

/** Sanity check used by the prerender build. */
export function assertSingleRootClaim(arches: SiteArche[] = SITE_ARCHES): void {
  const rootClaims = arches.filter((a) => a.servesDomainRoot);
  if (rootClaims.length > 1) {
    throw new Error(
      `Multiple site arches claim the domain root: ${rootClaims.map((a) => a.archeId).join(', ')}`,
    );
  }
}

/** The front page of the arche serving the domain root, if any. */
export function domainRootRoute(): { arche: SiteArche; route: SiteArcheRoute } | null {
  assertSingleRootClaim();
  const arche = SITE_ARCHES.find((a) => a.servesDomainRoot);
  if (!arche) return null;
  const route = arche.routes.find((r) => r.path === '/');
  if (!route) {
    throw new Error(`Site arche ${arche.archeId} serves the domain root but has no '/' route`);
  }
  return { arche, route };
}

export { SITE_ARCHE_PATH };
